import { startCase } from 'lodash';
import { usdFormatter } from 'utils/helpers';
import basicObjectMapper from 'utils/helpers/mappers/basicObjectMapper';
import { Company, Lead } from 'utils/types';

interface ContactDetails {
  label: string;
  value: string | number;
}

type ContactDetailsReducer = (
  acc: Record<string, ContactDetails>,
  arr: [key: string, value: any],
) => Record<string, ContactDetails>;

export const identifyAsCompany = (data: Record<string, any>) => ({
  ...data,
  type: 'company',
});

export const identifyAsLead = (data: Record<string, any>) => ({
  ...data,
  type: 'lead',
});

export const contactDetailsReducer: ContactDetailsReducer = (
  acc,
  [key, value],
) => {
  const excludedKeys = [
    'accountId',
    'id',
    'accountUserId',
    '_v',
    'accountUserId',
  ];
  const customLabels: Record<string, string> = {
    state: 'State/Region',
    linkedinUrl: 'LinkedIn Company Page',
    noEmployees: 'Number of employees',
  };

  const setLabels = (k: string) => customLabels?.[k] ?? startCase(k);

  if (excludedKeys.includes(key)) return acc;
  acc[key] = {
    label: setLabels(key),
    value: value || '---',
  };
  return acc;
};

export const contactDetailsFilterAndSorter = (contact: Record<string, any>) => {
  if (contact.type === 'lead') {
    return {
      emailAddress: contact.emailAddress,
      mobileNumber: contact.mobileNumber,
      businessNumber: contact.businessNumber,
    };
  }

  return {
    companyWebsite: contact?.companyWebsite,
    industry: startCase(contact?.industry),
    type: startCase(contact?.type),
    city: contact?.city,
    state: contact?.state,
    postalCode: contact?.postalCode,
    noEmployees: contact?.noEmployees,
    annualRevenue: usdFormatter.format(contact.annualRevenue),
    timezone: contact?.timezone,
    description: contact?.description,
    linkedinUrl: contact?.linkedinUrl,
  };
};

interface ContactProfileDetailsMapperParams {
  contact: Record<string, any>;
  activities: Record<string, any>;
  calls: Record<string, any>;
}

export const contactProfileDetailsMapper = ({
  contact = {},
  activities = {},
  calls = {},
}: ContactProfileDetailsMapperParams) => ({
  contactDetails: basicObjectMapper(contactDetailsFilterAndSorter(contact))(
    contactDetailsReducer,
  ),
  activities,
  calls,
});

/**
 * Function used to track and log current data before being passed to the next function.
 * Used for debugging only
 */
export const trace = (data: any) => data;

export const leadContactProfileDetailsMapper = (lead: Partial<Lead>) =>
  basicObjectMapper(contactDetailsFilterAndSorter(identifyAsLead(lead)))(
    contactDetailsReducer,
  );

export const companyContactProfileDetailsMapper = (company: Partial<Company>) =>
  basicObjectMapper(contactDetailsFilterAndSorter(identifyAsCompany(company)))(
    contactDetailsReducer,
  );
